// src/pages/TrainingSetup.jsx
// Setup di una sessione di ALLENAMENTO: circuito + per ogni dispositivo
// l'AUTO (anagrafica Auto) e il PILOTA (Partecipanti). Durante la sessione il
// pilota di un'auto può cambiare (turni di guida). Nessuna password: si
// condivide il link.
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import '../App.css';
import './RaceSetup.css';
import { Icon } from '../components/icons';
import LiveDeviceValue from '../components/LiveDeviceValue';
import Memo from '../components/Memo';
import CircuitPicker from '../components/CircuitPicker';
import PickField from '../components/PickField';
import { carItem, pilotItem, normDeviceId } from '../utils/crew';

const API_BASE = process.env.REACT_APP_API_BASE || (window.location.protocol === 'https:' ? '' : `http://${window.location.hostname}:5000`);
const WS_URL = process.env.REACT_APP_WS_URL || (window.location.protocol === 'https:' ? `wss://${window.location.hostname}/ws` : `ws://${window.location.hostname}:5001`);

export default function TrainingSetup({ onStart, savedId = null, onDismissSaved = () => {} }) {
  const navigate = useNavigate();
  const [circuits, setCircuits] = useState([]);
  const [pilots, setPilots] = useState([]);
  const [cars, setCars] = useState([]);
  const [loaded, setLoaded] = useState({ cars: false, pilots: false });

  const [selectedCircuit, setSelectedCircuit] = useState(null);
  const [circuitData, setCircuitData] = useState(null);

  const [starting, setStarting] = useState(false);

  const [activeDevices, setActiveDevices] = useState({});   // presenza e stato (fix, fascia)
  const liveRef = useRef({});                                // valori vivi per dispositivo
  const shownRef = useRef({});                               // copia di activeDevices per il confronto
  const [crew, setCrew] = useState({});                      // MAC → { carId, pilotId }
  const [deviceAliases, setDeviceAliases] = useState({});

  const wsRef = useRef(null);
  const carsRef = useRef([]);
  const aliasesRef = useRef({});
  const crewRef = useRef({});

  useEffect(() => { carsRef.current = cars; }, [cars]);
  useEffect(() => { aliasesRef.current = deviceAliases; }, [deviceAliases]);
  useEffect(() => { crewRef.current = crew; }, [crew]);

  // un'auto e un pilota stanno su un solo dispositivo: se erano rimasti su un
  // dispositivo spento (non più in elenco), passano a quello scelto
  const setCrewField = (mac, key, id) => setCrew(prev => {
    const next = { ...prev };
    if (id) for (const m of Object.keys(next)) if (m !== mac && String(next[m]?.[key]) === String(id)) next[m] = { ...next[m], [key]: null };
    next[mac] = { ...(next[mac] || {}), [key]: id || null };
    return next;
  });

  // Assegnazione automatica dell'auto: il numero stampato sul dispositivo
  // (alias) è quello indicato nell'anagrafica dell'auto (ID dispositivo).
  const autoAssign = useCallback((mac) => {
    if (crewRef.current[mac]?.carId) return;
    const devId = normDeviceId(aliasesRef.current[String(mac).toUpperCase()]);
    if (!devId) return;
    const car = carsRef.current.find(c => normDeviceId(c.deviceId) === devId);
    if (!car) return;
    // un'auto su un solo dispositivo: se è già in elenco non la spostiamo
    if (Object.values(crewRef.current).some(v => v?.carId === String(car.id))) return;
    setCrew(prev => (prev[mac]?.carId ? prev : { ...prev, [mac]: { ...(prev[mac] || {}), carId: String(car.id) } }));
  }, []);

  // auto e piloti: all'apertura e quando si torna su questa scheda (creati in
  // un'altra scheda, compaiono senza ricaricare la pagina)
  const loadCrewLists = useCallback(() => {
    fetch(`${API_BASE}/api/pilots`).then(r => r.json())
      .then(j => { if (Array.isArray(j)) setPilots(j); }).catch(console.error)
      .finally(() => setLoaded(l => ({ ...l, pilots: true })));
    fetch(`${API_BASE}/api/cars`).then(r => r.json())
      .then(j => { if (Array.isArray(j)) setCars(j); }).catch(console.error)
      .finally(() => setLoaded(l => ({ ...l, cars: true })));
  }, []);
  useEffect(() => {
    fetch(`${API_BASE}/api/circuits`).then(r => r.json()).then(setCircuits).catch(console.error);
    loadCrewLists();
    fetch(`${API_BASE}/api/device-aliases`).then(r => r.json())
      .then(a => setDeviceAliases(a && typeof a === 'object' ? a : {})).catch(() => { });
    window.addEventListener('focus', loadCrewLists);
    return () => window.removeEventListener('focus', loadCrewLists);
  }, [loadCrewLists]);

  // anagrafiche arrivate dopo i dispositivi: si riprova l'assegnazione
  useEffect(() => {
    Object.keys(shownRef.current).forEach(autoAssign);
  }, [cars, deviceAliases, autoAssign]);

  useEffect(() => {
    if (!selectedCircuit) return;
    fetch(`${API_BASE}/api/circuits/${selectedCircuit}`).then(r => r.json()).then(setCircuitData).catch(console.error);
  }, [selectedCircuit]);

  useEffect(() => {
    // Connessione con riconnessione automatica (vedi RaceSetup).
    let cancelled = false;
    let reconnectTimer = null;
    let ws = null;
    // Pacchetti GPS dei dispositivi (fino a 15 al secondo ciascuno): i valori
    // vivi (velocità, battito) restano in liveRef e li mostra LiveDeviceValue;
    // la pagina si ridisegna solo quando un dispositivo compare, sparisce o
    // cambia stato (fix GPS, fascia cardio). Ridisegnarla a ogni pacchetto
    // teneva il browser sempre occupato e il menu non cambiava più pagina.
    const live = liveRef.current;
    const flushDevices = () => {
      const now = Date.now();
      // spento o fuori copertura da più di 6 s: fuori elenco
      for (const mac of Object.keys(live)) if (now - live[mac].lastSeen > 6000) delete live[mac];
      // confronto qui, fuori da React: se nulla è cambiato non si chiede
      // nemmeno l'aggiornamento (React rieseguirebbe comunque la pagina)
      const prev = shownRef.current;
      const macs = Object.keys(live);
      let changed = Object.keys(prev).length !== macs.length;
      const next = {};
      const fresh = [];
      for (const mac of macs) {
        const d = live[mac], p = prev[mac];
        const st = { fix: d.fix, hasHr: d.hr != null, beaconId: d.beaconId };
        if (!p) { fresh.push(mac); next[mac] = st; changed = true; continue; }
        if (p.fix !== st.fix || p.hasHr !== st.hasHr || p.beaconId !== st.beaconId) { next[mac] = st; changed = true; } else next[mac] = p;
      }
      if (!changed) return;
      shownRef.current = next;
      setActiveDevices(next);
      fresh.forEach(autoAssign);
    };
    const flushTimer = setInterval(flushDevices, 500);

    const handleMessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        // Device vivo anche senza fix GPS (ai box): il server manda fix:false e
        // coordinate nulle; lo mostriamo comunque, con lo stato della fascia cardio.
        if (data?.type === 'gps_raw' && data?.data?.mac) {
          const { mac, lat, lon, speedKmh, hr, beaconId } = data.data;   // lat/lon: solo per il fix
          const hasFix = data.data.fix !== false && Number.isFinite(Number(lat)) && Number.isFinite(Number(lon)) && !(Math.abs(Number(lat)) < 0.01 && Math.abs(Number(lon)) < 0.01);
          const speed = hasFix ? Number(speedKmh || 0) : 0;
          const hrVal = (Number.isFinite(Number(hr)) && Number(hr) > 0) ? Number(hr) : null;
          const beaconVal = (Number.isFinite(Number(beaconId)) && Number(beaconId) > 0) ? Number(beaconId) : null;
          live[mac] = { lastSeen: Date.now(), speed, fix: hasFix, hr: hrVal, beaconId: beaconVal };
        }
      } catch { }
    };
    const connect = () => {
      if (cancelled) return;
      ws = new WebSocket(WS_URL);
      wsRef.current = ws;
      ws.onmessage = handleMessage;
      ws.onerror = e => console.error('[TrainingSetup] WS errore', e);
      ws.onclose = () => {
        if (cancelled) return;
        reconnectTimer = setTimeout(connect, 1500);
      };
    };
    connect();

    return () => {
      cancelled = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      clearInterval(flushTimer);
      try { ws && ws.close(); } catch { }
    };
  }, [autoAssign]);   // autoAssign non cambia mai (useCallback senza dipendenze)

  const activeMacs = Object.keys(activeDevices);
  const deviceLabel = (mac) => {
    const id = deviceAliases[String(mac).toUpperCase()];
    return id != null && String(id).trim() !== '' ? `ID ${String(id).trim()}` : mac;
  };
  // equipaggi completi (auto + pilota): sono i dispositivi seguiti
  const readyCrew = Object.fromEntries(Object.entries(crew)
    .filter(([, c]) => c?.carId && c?.pilotId)
    .map(([mac, c]) => [mac, { carId: c.carId, pilotId: c.pilotId }]));
  const assignedCount = Object.keys(readyCrew).length;

  const handleStart = async () => {
    if (!selectedCircuit) return alert('ATTENZIONE: seleziona un circuito!');
    // dispositivo acceso con solo l'auto o solo il pilota: va completato
    const half = activeMacs.find(mac => { const c = crew[mac]; return c && !!c.carId !== !!c.pilotId; });
    if (half) {
      const c = crew[half];
      return alert(`ATTENZIONE: per il dispositivo ${deviceLabel(half)} manca ${c.carId ? 'il pilota' : 'l\'auto'}. Completa la scelta oppure svuota anche l'altro campo.`);
    }
    if (assignedCount === 0) return alert('ATTENZIONE: scegli l\'auto e il pilota per almeno un dispositivo acceso!');

    setStarting(true);
    try {
      const body = {
        circuitId: selectedCircuit,
        crew: readyCrew,
      };
      const r = await fetch(`${API_BASE}/api/training/start`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body)
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j?.error || 'Errore avvio allenamento');
      onStart({
        trainingId: j.trainingId,
        token: j.token,
        circuitData,
      });
    } catch (e) {
      console.error(e);
      alert('Errore nell\'avvio dell\'allenamento: ' + e.message);
    } finally {
      setStarting(false);
    }
  };

  const selectedCircuitName = circuits.find(c => c.id === selectedCircuit)?.name || '';
  const startSummary = selectedCircuit && assignedCount > 0
    ? `${assignedCount} ${assignedCount === 1 ? 'auto pronta' : 'auto pronte'}`
    : 'Seleziona circuito, auto e piloti';

  // voci dei campi: un'auto e un pilota su un solo dispositivo
  const usedBy = (key) => {
    const m = new Map();
    Object.entries(crew).forEach(([mac, c]) => { if (c?.[key]) m.set(String(c[key]), mac); });
    return m;
  };
  const carsUsed = usedBy('carId');
  const pilotsUsed = usedBy('pilotId');
  const carItemsFor = (mac) => cars.map(c => {
    const other = carsUsed.get(String(c.id));
    return other && other !== mac && activeDevices[other] ? { ...carItem(c), disabled: true, note: `già sul dispositivo ${deviceLabel(other)}` } : carItem(c);
  });
  const pilotItemsFor = (mac) => pilots.map(p => {
    const other = pilotsUsed.get(String(p.id));
    const carName = other ? (cars.find(c => String(c.id) === String(crew[other]?.carId))?.name || `dispositivo ${deviceLabel(other)}`) : '';
    return other && other !== mac && activeDevices[other] ? { ...pilotItem(p), disabled: true, note: `già su ${carName}` } : pilotItem(p);
  });

  return (
    <div className="rs-page setup-page setup-page--training">

      {/* ═══ HEADER ═══ */}
      <header className="rs-page-header">
        <div className="rs-page-header__text">
          <span className="rs-kicker rs-kicker--lime"><span className="rs-kicker__dot rs-kicker__dot--lime" />Sessione · Allenamento</span>
          <h1 className="rs-page-header__title">Sessione di <em>allenamento</em></h1>
          <p className="rs-page-header__subtitle">Scegli il circuito e, per ogni dispositivo acceso, l'auto e il pilota. Durante la sessione puoi cambiare pilota quando vuoi.</p>
        </div>
        <div className="rs-page-header__actions">
          <div className="setup-start">
            <button type="button" className="rs-btn rs-btn--lime rs-btn--xl" onClick={handleStart} disabled={starting}>
              <Icon.Play size={18} /> {starting ? 'Avvio…' : 'Avvia allenamento'}
            </button>
            <span className="setup-start__hint">{startSummary}</span>
          </div>
        </div>
      </header>

      {/* sessione appena terminata: riepilogo con i turni di guida e i report */}
      {savedId && (
        <div className="rs-alert rs-alert--success setup-saved">
          <Icon.Check />
          <span><strong>Allenamento salvato.</strong> Nel riepilogo trovi chi ha guidato ogni auto e quando, con il report PDF di ogni turno.</span>
          <button type="button" className="rs-btn rs-btn--sm" onClick={() => navigate(`/training/${encodeURIComponent(savedId)}`)}>
            Apri il riepilogo <Icon.ArrowRight size={14} />
          </button>
          <button type="button" className="rs-btn rs-btn--ghost rs-btn--sm rs-btn--icon" onClick={onDismissSaved} aria-label="Chiudi l'avviso"><Icon.X size={14} /></button>
        </div>
      )}

      {/* ═══ STAT ═══ */}
      <section className="setup-stats" aria-label="Riepilogo configurazione">
        <div className="rs-stat">
          <span className="rs-stat__label">Circuiti</span>
          <span className="rs-stat__value">{circuits.length}</span>
          <span className="rs-stat__hint rs-truncate">{selectedCircuitName ? `Selezionato: ${selectedCircuitName}` : 'Nessun circuito selezionato'}</span>
        </div>
        <div className={`rs-stat ${activeMacs.length ? 'rs-stat--lime' : ''}`}>
          <span className="rs-stat__label">Device attivi</span>
          <span className="rs-stat__value">{activeMacs.length}</span>
          <span className="rs-stat__hint">GPS in ricezione negli ultimi 6 s</span>
        </div>
        <div className={`rs-stat ${assignedCount ? 'rs-stat--lime' : ''}`}>
          <span className="rs-stat__label">Pronti</span>
          <span className="rs-stat__value">{assignedCount}<small>/ {activeMacs.length} device</small></span>
          <span className="rs-stat__hint">{assignedCount ? 'Auto con il pilota scelto' : 'Scegli auto e pilota'}</span>
        </div>
      </section>

      <div className="setup-stack">

        {/* ═══ CIRCUITO ═══ */}
        <section className="rs-card">
          <div className="rs-card__head setup-card-head">
            <span className="rs-card__title rs-card__title--lime"><Icon.Track /> Circuito</span>
            {selectedCircuitName
              ? <span className="rs-badge rs-badge--lime"><Icon.Check size={12} /> {selectedCircuitName}</span>
              : <span className="rs-badge">Nessuno selezionato</span>}
          </div>
          <div className="rs-card__body">
            <CircuitPicker
              circuits={circuits}
              value={selectedCircuit}
              onChange={setSelectedCircuit}
              tone="lime"
              emptyText="Crea un tracciato dalla sezione Circuiti prima di avviare una sessione."
            />
          </div>
        </section>

        {/* ═══ DEVICE DA SEGUIRE: AUTO + PILOTA ═══ */}
        <section className="rs-card">
          <div className="rs-card__head setup-card-head">
            <span className="rs-card__title rs-card__title--lime"><Icon.Device /> Auto e piloti</span>
            <span className={`rs-badge ${activeMacs.length ? 'rs-badge--lime' : ''}`}>
              <span className={`rs-badge__dot ${activeMacs.length ? 'rs-badge__dot--live' : ''}`} />
              {activeMacs.length} online
            </span>
          </div>
          <div className="rs-card__body">
            {loaded.cars && cars.length === 0 && (
              <div className="rs-alert rs-alert--warning setup-crew__alert">
                <Icon.Alert />
                <span>Non ci sono auto in anagrafica: crea le auto su cui monti i dispositivi.</span>
                <button type="button" className="rs-btn rs-btn--sm" onClick={() => navigate('/auto')}><Icon.Car size={14} /> Vai ad Auto</button>
              </div>
            )}
            {loaded.pilots && pilots.length === 0 && (
              <div className="rs-alert rs-alert--warning setup-crew__alert">
                <Icon.Alert />
                <span>Non ci sono piloti in anagrafica: crea i piloti in Partecipanti.</span>
                <button type="button" className="rs-btn rs-btn--sm" onClick={() => navigate('/piloti')}><Icon.Users size={14} /> Vai a Partecipanti</button>
              </div>
            )}
            {activeMacs.length === 0 ? (
              <div className="rs-empty">
                <Icon.Device />
                <strong>Nessun device GPS attivo</strong>
                <p>Accendi i dispositivi e mettili sul vetro: compaiono qui appena inviano posizione.</p>
              </div>
            ) : (
              <div className="setup-devices">
                {activeMacs.map(mac => {
                  const c = crew[mac] || {};
                  const ready = !!(c.carId && c.pilotId);
                  const half = !ready && !!(c.carId || c.pilotId);
                  const devId = deviceAliases[String(mac).toUpperCase()];
                  const hasDevId = devId != null && String(devId).trim() !== '';
                  const dev = activeDevices[mac] || {};
                  // scheda ridisegnata solo se cambiano i suoi dati (vedi Memo)
                  return (
                    <Memo key={mac} deps={[activeDevices, crew, cars, pilots, deviceAliases]} render={() => (
                    <div className={`setup-device setup-device--crew ${ready ? 'is-assigned' : ''} ${half ? 'is-half' : ''}`}>
                      <div className="setup-device__head">
                        <span className="setup-device__id" title={mac}>
                          <span className="setup-device__dot" />
                          {hasDevId ? <><span className="setup-device__tag">ID</span>{String(devId).trim()}</> : mac}
                        </span>
                        {ready
                          ? <span className="rs-badge rs-badge--lime"><Icon.Check size={12} /> Pronto</span>
                          : half
                            ? <span className="rs-badge rs-badge--warning">Da completare</span>
                            : <span className="rs-badge rs-badge--outline">Libero</span>}
                      </div>

                      <div className="setup-device__chips">
                        <span className="rs-chip"><Icon.Zap /><strong><LiveDeviceValue store={liveRef} mac={mac} field="speed" format={v => Math.round(v || 0)} /></strong> km/h</span>
                        {dev.fix === false
                          ? <span className="rs-chip setup-chip--nofix" title="Il dispositivo trasmette ma non ha ancora il fix GPS"><Icon.Radio /><strong>GPS</strong> no fix</span>
                          : <span className="rs-chip"><Icon.Radio /><strong>GPS</strong> fix</span>}
                        {dev.hasHr
                          ? <span className="rs-chip setup-chip--hr" title={`Fascia cardio #${dev.beaconId ?? '—'}`}><Icon.Heart /><strong><LiveDeviceValue store={liveRef} mac={mac} field="hr" format={v => v ?? '—'} /></strong> bpm</span>
                          : dev.beaconId
                            ? <span className="rs-chip setup-chip--hr is-idle" title="Fascia agganciata, in attesa del battito"><Icon.Heart />#{dev.beaconId}</span>
                            : null}
                        {hasDevId && <span className="rs-chip setup-device__mac" title={mac}><Icon.Cpu />{mac}</span>}
                      </div>

                      <div className="setup-device__body setup-crew">
                        <div className="setup-crew__field">
                          <span className="setup-crew__label"><Icon.Car size={13} /> Auto</span>
                          <PickField
                            items={carItemsFor(mac)}
                            value={c.carId || null}
                            onChange={(id) => setCrewField(mac, 'carId', id)}
                            placeholder="Scegli l'auto…"
                            emptyText={cars.length ? 'Nessuna auto trovata' : 'Nessuna auto: creale nella sezione Auto'}
                            icon={Icon.Car}
                            clearLabel="Cambia auto"
                          />
                        </div>
                        <div className="setup-crew__field">
                          <span className="setup-crew__label"><Icon.Users size={13} /> Pilota</span>
                          <PickField
                            items={pilotItemsFor(mac)}
                            value={c.pilotId || null}
                            onChange={(id) => setCrewField(mac, 'pilotId', id)}
                            placeholder="Scegli il pilota…"
                            emptyText={pilots.length ? 'Nessun pilota trovato' : 'Nessun pilota: crealo in Partecipanti'}
                            icon={Icon.Users}
                            clearLabel="Cambia pilota"
                          />
                        </div>
                      </div>
                    </div>
                    )} />
                  );
                })}
              </div>
            )}
          </div>
        </section>

        {/* ═══ AVVIO (in fondo, stesso handler dell'header) ═══ */}
        <div className="rs-card setup-footer">
          <div className="setup-footer__text">
            <span className="rs-kicker rs-kicker--lime">Pronti al via</span>
            <strong>{startSummary}</strong>
          </div>
          <button type="button" className="rs-btn rs-btn--lime rs-btn--xl" onClick={handleStart} disabled={starting}>
            <Icon.Play size={18} /> {starting ? 'Avvio…' : 'Avvia allenamento'}
          </button>
        </div>
      </div>
    </div>
  );
}
